import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  AddressWrapper,
  PinContentStyle,
} from "../addMember/addUsers/advanceSearch/map/AdvanceSearchMap.style";
import { DEFAULT_ZOOM as USA_DEFAULT_ZOOM } from "../addPatientGroup/addressField/AddressMapHelper";
import { ClinicianMapDialog } from "../common/clinicianMapDialog/ClinicianMapDialog";
import { QUERY_ROUTES } from "../routes";
import CareMapHeaderContent from "./CareMapHeaderContent";
import { useGeocodedAddress } from "./useGeocodedAddress";

interface CareMapDialogProps {
  closeModal: () => void;
}

export const CareMapDialog = ({ closeModal }: CareMapDialogProps) => {
  const [query] = useSearchParams();
  const addressFromUrl = query.get(QUERY_ROUTES.ADDRESS);

  const [searchCoordinates, setSearchCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [formattedAddress, setFormattedAddress] = useState("");

  const { status: geocodeStatus, result: geocodedAddress } =
    useGeocodedAddress(addressFromUrl);

  useEffect(() => {
    if (!geocodedAddress) return;
    setSearchCoordinates(geocodedAddress.coordinates);
    setFormattedAddress(geocodedAddress.formattedAddress);
  }, [geocodedAddress]);

  const advanceSearchFilters = useMemo(
    () =>
      searchCoordinates
        ? { lat: searchCoordinates.lat, lng: searchCoordinates.lng }
        : undefined,
    [searchCoordinates],
  );

  const hasSearched = searchCoordinates !== null;

  const onPlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setSearchCoordinates({ lat, lng });
        setFormattedAddress(place.formatted_address || "");
      } else {
        setSearchCoordinates(null);
        setFormattedAddress("");
      }
    },
    [],
  );

  return (
    <ClinicianMapDialog
      closeModal={closeModal}
      title="careMap"
      testId="care-map-dialog"
      advanceSearchFilters={advanceSearchFilters}
      headerContent={
        <CareMapHeaderContent
          formattedAddress={formattedAddress}
          onPlaceSelected={onPlaceSelected}
          hasSearched={hasSearched}
          geocodeStatus={geocodeStatus}
        />
      }
      centerPinContent={
        hasSearched ? (
          <PinContentStyle>
            <AddressWrapper variant="caption">
              {formattedAddress}
            </AddressWrapper>
          </PinContentStyle>
        ) : undefined
      }
      defaultZoom={USA_DEFAULT_ZOOM}
    />
  );
};
